import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import NotificationItem from "components/notifications/notificationItem";
import { UserNotification } from "shared/interfaces/notifications";
import { List, CellMeasurer, CellMeasurerCache, ListRowRenderer } from "react-virtualized";
import { usePopoverStyles } from "components/notifications/notificationsButton/styles";
import { DEFAULT_NOTIFICATION_ROW_HEIGHT_PX, NOTIFICATIONS_LIST_MAX_HEIGHT_PX } from "shared/constants/appConstants";
import { Theme, useMediaQuery } from "@material-ui/core";
import { mediumBreakpoint, mobileBreakpoint, tabletBreakpoint } from "shared/theme/defaultTheme";
import clsx from "clsx";

interface NotificationsListProps {
    notifications: Array<UserNotification>;
    onDeleteNotification: (index: number) => Promise<void>;
    containerRef: React.MutableRefObject<HTMLDivElement | null>;
    close: () => Promise<void>;
}

interface VirutialListRequiredData {
    width: number;
}

const MOBILE_LIST_HEIGHT_ADJUSTMENT_PX = 100;
const UPPER_MOBILE_LIST_HEIGHT_ADJUSTMENT = 150;

const NotificationsList = ({
    notifications,
    onDeleteNotification,
    containerRef,
    close,
}: NotificationsListProps): JSX.Element => {
    const classes = usePopoverStyles();
    const [virtualListRequiredData, setVirtualListRequiredData] = useState<VirutialListRequiredData | null>(null);
    const [totalRowsHeight, setTotalRowsHeight] = useState(0);
    const virtualListRef = useRef<List>(null);
    const [calculatedListHeight, setCalculatedListHeight] = useState(0);
    const isMobileBreakpoint = useMediaQuery<Theme>(theme => theme.breakpoints.only(mobileBreakpoint));
    const isTabletBreakpoint = useMediaQuery<Theme>(theme => theme.breakpoints.only(tabletBreakpoint));
    const isMediumBreakpoint = useMediaQuery<Theme>(theme => theme.breakpoints.only(mediumBreakpoint));
    const { matches: isPortraitOrientation } = window.matchMedia("(orientation: portrait)");

    const calculatedHeight = useMemo(() => {
        const { documentElement: html } = document;

        if (isMobileBreakpoint) return html.clientHeight - MOBILE_LIST_HEIGHT_ADJUSTMENT_PX;
        if (isTabletBreakpoint || isMediumBreakpoint) return html.clientHeight - UPPER_MOBILE_LIST_HEIGHT_ADJUSTMENT;

        return NOTIFICATIONS_LIST_MAX_HEIGHT_PX;
    }, [isMobileBreakpoint, isTabletBreakpoint, isMediumBreakpoint]);

    useEffect(() => {
        const handleOrientationChange = () => {
            close();
        };
        window.addEventListener("orientationchange", handleOrientationChange);

        return () => {
            window.removeEventListener("orientationchange", handleOrientationChange);
        };
    });

    useEffect(() => {
        const r = containerRef;
        if (!r.current) return;

        if (totalRowsHeight < NOTIFICATIONS_LIST_MAX_HEIGHT_PX) {
            r.current.style.maxHeight = `${totalRowsHeight || NOTIFICATIONS_LIST_MAX_HEIGHT_PX}px`;
            setCalculatedListHeight(totalRowsHeight || NOTIFICATIONS_LIST_MAX_HEIGHT_PX);
            return;
        }

        // means current screen orientation is landscape
        if (!isPortraitOrientation) {
            r.current.style.maxHeight = `${calculatedHeight}px`;
            setCalculatedListHeight(calculatedHeight);
            return;
        }

        if (isMobileBreakpoint) {
            r.current.style.maxHeight = `${calculatedHeight}px`;
            setCalculatedListHeight(calculatedHeight);
        }
    }, [totalRowsHeight, containerRef, isMobileBreakpoint, calculatedHeight, isPortraitOrientation]);

    const cache = useMemo(
        () =>
            new CellMeasurerCache({
                defaultHeight: DEFAULT_NOTIFICATION_ROW_HEIGHT_PX,
                minHeight: DEFAULT_NOTIFICATION_ROW_HEIGHT_PX,
            }),
        // this needs to get correct cache value for virtual list's rows heights
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [notifications]
    );

    useEffect(() => {
        if (!containerRef.current) return;

        const { current: listContainer } = containerRef;

        setVirtualListRequiredData({
            width: listContainer.offsetWidth,
        });
    }, [containerRef]);

    const onVirtualListRowsRendered = useCallback(() => {
        const totalHeight = notifications.reduce((acc, _, index) => {
            return acc + cache.rowHeight({ index });
        }, 0);

        setTotalRowsHeight(totalHeight);
    }, [cache, notifications]);

    const handleDeleteNotification = useCallback(
        async (index: number) => {
            await onDeleteNotification(index);
            virtualListRef.current?.scrollToPosition(0);
        },
        [onDeleteNotification]
    );

    const virtualListRowRenderer: ListRowRenderer = useCallback(
        ({ key, index, style, parent }) => {
            const item = notifications[index];
            return (
                <CellMeasurer parent={parent} cache={cache} columnIndex={0} key={key} rowIndex={index}>
                    <NotificationItem
                        key={key}
                        index={index}
                        title={item.title}
                        created={item.createdText}
                        isRead={item.isRead}
                        description={item.text}
                        onDeleteNotification={handleDeleteNotification}
                        style={style}
                    />
                </CellMeasurer>
            );
        },
        [cache, notifications, handleDeleteNotification]
    );

    if (!virtualListRequiredData) return <></>;

    return (
        <>
            <List
                ref={virtualListRef}
                width={virtualListRequiredData.width}
                height={calculatedListHeight || 656}
                onRowsRendered={onVirtualListRowsRendered}
                rowCount={notifications.length}
                rowHeight={cache.rowHeight}
                className={clsx(classes.listRoot, classes.listScrollStyles)}
                rowRenderer={virtualListRowRenderer}
            />
        </>
    );
};

export default NotificationsList;
